/**
 * The revision history of one position (CLAUDE.md 5.7, 2.2).
 *
 * A revision is recorded each time the inputs are saved, and each one records the
 * module version and parameter-set version it ran under -- two of the four pinned
 * identifiers. Showing them here is the point: an engineer looking at an old revision
 * must be able to see that it was calculated under a different version, because
 * otherwise "the numbers used to be different" has no explanation.
 *
 * Restoring is where the care goes. It is not an undo. It puts the *inputs* back and
 * nothing else, and the confirmation says all four consequences plainly rather than
 * asking "are you sure?" -- which tells the reader nothing they did not already know.
 */
import { useState } from 'react'
import { History, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useRestoreRevision, useRevisionDetail, useRevisions } from '@/api/hooks'
import type { Position } from '@/api/types'
import { InputDiff } from '@/components/calc/InputDiff'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/cn'
import { formatRelative } from '@/lib/format'

export function RevisionsPanel({ position }: { position: Position }) {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()

  const { data: revisions, isLoading } = useRevisions(position.id)
  const [openRev, setOpenRev] = useState<number | null>(null)
  const [confirmRev, setConfirmRev] = useState<number | null>(null)
  const { data: detail, isLoading: detailLoading } = useRevisionDetail(position.id, openRev)
  const restore = useRestoreRevision(position.id)

  // Newest first: the interesting revision is almost always the last one.
  const rows = [...(revisions ?? [])].sort((a, b) => b.rev - a.rev)

  const onRestore = async (rev: number) => {
    try {
      await restore.mutateAsync(rev)
      setConfirmRev(null)
      setOpenRev(null)
      toast({ title: t('revisions.restored'), variant: 'success' })
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <History size={15} className="shrink-0 text-text-muted" aria-hidden />
        <CardTitle>{t('revisions.title')}</CardTitle>
      </CardHeader>

      <CardBody className="space-y-3">
        {isLoading && <Skeleton className="h-16 w-full" />}

        {!isLoading && rows.length === 0 && (
          <p className="text-sm text-text-muted">{t('revisions.empty')}</p>
        )}

        <ul className="space-y-2">
          {rows.map((revision) => {
            const open = openRev === revision.rev
            return (
              <li key={revision.id} className="rounded-md border border-border">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpenRev(open ? null : revision.rev)}
                  className={cn(
                    'flex w-full flex-col items-start gap-1 p-3 text-start',
                    'min-h-[2.75rem] sm:flex-row sm:items-center sm:gap-3',
                    open ? 'bg-surface-raised' : 'hover:bg-surface-raised',
                  )}
                >
                  <span className="tabular shrink-0 text-sm font-medium">
                    {t('revisions.rev', { rev: revision.rev })}
                  </span>
                  <span className="text-xs text-text-muted">
                    {formatRelative(revision.created_at, i18n.language)}
                  </span>
                  {/* Two of the four pinned identifiers, as they were at the time
                      (5.7). Versions are identifiers and are never translated. */}
                  <span className="tabular text-xs text-text-muted sm:ms-auto">
                    {t('revisions.ran_under', {
                      version: revision.module_version,
                      paramSet: revision.param_set_version,
                    })}
                  </span>
                </button>

                {open && (
                  <div className="space-y-3 border-t border-border p-3">
                    <p className="text-xs text-text-muted">
                      {t('revisions.compare_hint', { rev: revision.rev })}
                    </p>

                    {detailLoading && <Skeleton className="h-16 w-full" />}

                    {detail && detail.rev === revision.rev && (
                      <InputDiff before={detail.inputs} after={position.inputs} />
                    )}

                    <Button
                      size="sm"
                      onClick={() => setConfirmRev(revision.rev)}
                      disabled={restore.isPending}
                    >
                      <RotateCcw size={14} aria-hidden />
                      {t('revisions.restore')}
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </CardBody>

      <Dialog open={confirmRev !== null} onOpenChange={(open) => !open && setConfirmRev(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('revisions.restore_title', { rev: confirmRev ?? 0 })}</DialogTitle>
            <DialogDescription>{t('revisions.restore_intro')}</DialogDescription>
          </DialogHeader>

          {/*
            All four consequences, stated. Section 5.7 makes the stored result and the
            pinned versions a guarantee, and section 2.2 makes the review state a
            professional-responsibility record; a restore touches both, so a reader who
            has not been told exactly what happens cannot consent to it.
          */}
          <ul className="list-disc space-y-2 ps-5 text-sm text-text">
            <li>{t('revisions.restore_consequence_inputs')}</li>
            <li>{t('revisions.restore_consequence_result')}</li>
            <li>{t('revisions.restore_consequence_review')}</li>
            <li>{t('revisions.restore_consequence_stale')}</li>
          </ul>

          <DialogFooter>
            <Button type="button" onClick={() => setConfirmRev(null)}>
              {t('action.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={restore.isPending}
              onClick={() => confirmRev !== null && void onRestore(confirmRev)}
            >
              <RotateCcw size={15} aria-hidden />
              {t('revisions.restore')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
